/** Small presentational pieces. Props in, callbacks out; no data fetching here. */
import {
  commissionStatusLabel,
  leadStatusLabel,
  lifecycleLabel,
  policyStageLabel,
  postSaleStatusLabel,
  type CommissionStatus,
  type LeadStatus,
  type LifecycleStage,
  type PolicyStage,
  type PostSaleStatus,
} from "@tlcrm/shared";
import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { Icon } from "./Icon";
import type { IconName } from "./icons";

export type Tone = "accent" | "ochre" | "brick" | "mute" | "ink";

/** A status as a square mark and a word. The word is the point; the colour only helps. */
export function Mark({ tone, children }: { tone: Tone; children: ReactNode }) {
  return <span className={`mark mark-${tone}`}>{children}</span>;
}

const LEAD_TONE: Record<LeadStatus, Tone> = {
  new: "accent",
  working: "ink",
  contacted: "ink",
  converted: "accent",
  disqualified: "mute",
};
export const LeadStatusMark = ({ status }: { status: LeadStatus }) => (
  <Mark tone={LEAD_TONE[status]}>{leadStatusLabel(status)}</Mark>
);

const STAGE_TONE: Record<PolicyStage, Tone> = {
  qualification: "mute",
  needs_analysis: "mute",
  proposal: "mute",
  issued: "ink",
  closed_won: "accent",
  closed_lost: "mute",
};
/** Stage ("On Issue or Draft?"): Issued is sold and waiting on the first draft, not a warning. */
export const StageMark = ({ stage }: { stage: PolicyStage }) => (
  <Mark tone={STAGE_TONE[stage]}>{policyStageLabel(stage)}</Mark>
);

const POST_SALE_TONE: Record<PostSaleStatus, Tone> = {
  active: "ink",
  cancelled: "brick",
  not_taken: "ochre",
};
export const PostSaleMark = ({ status }: { status: PostSaleStatus }) => (
  <Mark tone={POST_SALE_TONE[status]}>{postSaleStatusLabel(status)}</Mark>
);

/** Shown only when the commission could not be worked out: a calculated one needs no badge. */
export function CommissionFlag({
  status,
  short,
}: {
  status: CommissionStatus | null;
  /** In a table column: "No rule", with the full words on hover. */
  short?: boolean;
}) {
  if (!status || status === "calculated") return null;
  return (
    <span title={commissionStatusLabel(status)}>
      <Mark tone="ochre">
        {short
          ? status === "no_rule"
            ? "No rule"
            : "Inputs missing"
          : commissionStatusLabel(status)}
      </Mark>
    </span>
  );
}

const LIFECYCLE_PILL: Record<LifecycleStage, string> = {
  prospect: "pill",
  client: "pill pill-accent",
  former_client: "pill",
  related: "pill",
};
export const LifecyclePill = ({ stage }: { stage: LifecycleStage }) => (
  <span className={LIFECYCLE_PILL[stage]}>{lifecycleLabel(stage)}</span>
);

export function DncTag() {
  return (
    <span className="dnc" title="On the do-not-call list">
      <Icon name="ban" />
      DNC
    </span>
  );
}

export function EmptyState({
  icon = "info",
  title,
  children,
}: {
  icon?: IconName;
  title: string;
  children?: ReactNode;
}) {
  return (
    <div className="empty">
      <Icon name={icon} />
      <h4>{title}</h4>
      {children && <p>{children}</p>}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : "Something went wrong.";
  return (
    <div className="empty">
      <Icon name="alert" />
      <h4>Couldn't load this</h4>
      <p>{message}</p>
      {onRetry && (
        <button className="btn btn-sm" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

/* ---- Form controls ------------------------------------------------------------------------- */

export function FieldBox({
  label,
  error,
  full,
  children,
  htmlFor,
}: {
  label: string;
  error?: string;
  full?: boolean;
  children: ReactNode;
  htmlFor?: string;
}) {
  return (
    <div className={full ? "full" : undefined}>
      <label className="field-label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {error && <div className="field-error">{error}</div>}
    </div>
  );
}

export function Select<T extends string>({
  value,
  onChange,
  options,
  empty,
  id,
  invalid,
}: {
  value: T | "" | null;
  onChange: (v: T | "") => void;
  options: readonly (readonly [T, string])[];
  /** The label of the "nothing chosen" option; omit to require a choice. */
  empty?: string;
  id?: string;
  invalid?: boolean;
}) {
  return (
    <select
      id={id}
      className={`field${invalid ? " is-invalid" : ""}`}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value as T | "")}
    >
      {empty !== undefined && <option value="">{empty}</option>}
      {options.map(([v, l]) => (
        <option key={v} value={v}>
          {l}
        </option>
      ))}
    </select>
  );
}

/* ---- Modal -------------------------------------------------------------------------------- */

export function Modal({
  title,
  onClose,
  children,
  footer,
  width,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}) {
  const titleId = useId();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLElement>("input, select, textarea, button")?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      previous?.focus();
    };
  }, [onClose]);
  return createPortal(
    <>
      <div className="modal-scrim" onClick={onClose} />
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={ref}
        style={width ? { width: `min(${width}px, 92vw)` } : undefined}
      >
        <div className="modal-head">
          <h2 id={titleId}>{title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            <Icon name="x" />
          </button>
        </div>
        <div className="modal-body scroll">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </>,
    document.body,
  );
}
